import { create } from "zustand";
import type { Role, UUID, UserResponse } from "@/api/types";

/**
 * Lightweight current-user store. Only fields the UI shell or RBAC needs
 * live here; the full UserResponse stays in TanStack Query cache under the
 * `currentUser` query key so refetches don't go through Zustand.
 *
 * Setting `null` reverts to the initial state — equivalent to `clear()` —
 * which is critical for U0 gap §1c (no partial-state leaks).
 */
export interface AuthSlice {
  userId: UUID | null;
  email: string | null;
  role: Role | null;
  employeeId: UUID | null;
  isActive: boolean;
  setUser: (user: UserResponse | null) => void;
  clear: () => void;
}

const INITIAL = {
  userId: null,
  email: null,
  role: null,
  employeeId: null,
  isActive: false,
} as const;

export const useAuthStore = create<AuthSlice>((set) => ({
  ...INITIAL,
  setUser: (user) =>
    set(
      user === null
        ? { ...INITIAL }
        : {
            userId: user.id,
            email: user.email,
            role: user.role,
            employeeId: user.employee_id,
            isActive: user.is_active,
          },
    ),
  clear: () => set({ ...INITIAL }),
}));
